import { type } from 'arktype';
import type { TranslatableText } from '@zag/core/common';

export const ICON_TRANSLATABLE_FIELDS = ['label', 'description'] as const;
export type IconTranslatableField = (typeof ICON_TRANSLATABLE_FIELDS)[number];
export type IconTranslations = TranslatableText<IconTranslatableField>;

/**
 * Icon categories for grouping icons in the catalog
 */
export const iconCategories = [
  'people_identity',
  'org_structure',
  'skills_competence',
  'time_shift',
  'location_place',
  'operations_tools',
  'communication',
  'status_priority',
  'files_data',
  'food_hospitality',
  'retail_service',
  'healthcare',
  'transport_logistics',
  'misc_general',
] as const;

export type IconCategory = (typeof iconCategories)[number];

/**
 * Query schema for filtering icons list
 */
export const iconQuerySchema = type({
  'name?': type('string').configure({
    description: 'Filter icons by exact name. Optional.',
    examples: ['person'],
  }),
  'category?': type('string').configure({
    description:
      'Filter icons by category (e.g., people_identity, org_structure, skills_competence, time_shift, location_place, operations_tools, communication, status_priority, files_data, misc_general). Optional.',
    examples: ['people_identity'],
  }),
  'search?': type('string').configure({
    description: 'Free-text search across icon name, label, description, and tags. Optional.',
    examples: ['clock'],
  }),
  'isActive?': type('string').configure({
    description: 'Filter by active status. Accepts the string "true" or "false" (query-param encoded). Optional.',
    examples: ['true', 'false'],
  }),
}).configure({ description: 'Query parameters for filtering the icons catalog endpoint.' });

export type IconQuery = typeof iconQuerySchema.t;

/**
 * Response type for a single icon (matches DB schema)
 */
export interface IconResponse {
  id: string;
  name: string;
  label: string;
  description: string | null;
  category: string;
  tags: string[];
  isActive: boolean;
  sortOrder: number;
  /** Full per-language translations map. Only populated when the caller asked for it via `?include=translations`. */
  labelTranslations?: TranslatableText<'label'>;
  descriptionTranslations?: TranslatableText<'description'>;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Grouped icons response for icon picker UI
 */
export interface IconCategoryGroup {
  category: string;
  label: string;
  icons: IconResponse[];
}

/**
 * Category display labels
 */
export const iconCategoryLabels: Record<IconCategory, string> = {
  people_identity: 'People & Identity',
  org_structure: 'Organization',
  skills_competence: 'Skills & Competence',
  time_shift: 'Time & Shifts',
  location_place: 'Locations',
  operations_tools: 'Operations',
  communication: 'Communication',
  status_priority: 'Status & Priority',
  files_data: 'Files & Data',
  food_hospitality: 'Food & Hospitality',
  retail_service: 'Retail & Service',
  healthcare: 'Healthcare',
  transport_logistics: 'Transport & Logistics',
  misc_general: 'General',
};
